import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  LayoutChangeEvent,
  PanResponder,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import Svg, { Circle, Ellipse, G, Line, Path, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { colors, neu, radii, fonts } from '../../domain/tokens';

// ---------------------------------------------------------------------------
// Native replica of the web Charts pages:
//   - src/app/charts/page.tsx (list: create / open / delete, sorted by
//     lastModified desc)
//   - src/app/charts/[chartId]/page.tsx + src/components/charts/
//     FlowChartEditor.tsx (editor: shape nodes with a label, connecting
//     edges, per-node background/font color + font size/weight, resize,
//     delete)
//
// Web persists chart data in `localStorage['charts']` (see interfaces/
// chart.ts — no Supabase table backs this feature at all). Mobile has no
// localStorage, so this uses AsyncStorage under the SAME key ('charts') and
// the SAME JSON shape (id/name/nodes/edges/lastModified, nodes shaped like
// reactflow's { id, type, position, data }, edges like { id, source,
// target }), so the persisted record is structurally identical to web's —
// only the storage engine differs, per platform.
//
// Charts are hand-rendered with react-native-svg (no reactflow — RN has no
// DOM/canvas). Node dragging is done with PanResponder (a core
// React Native API, no extra dependency), tap-to-connect stands in for
// reactflow's edge-drag-between-handles interaction, and node color/shape
// editing is exposed via an action-sheet ("⋯" on the selected node) — the
// mobile-appropriate analogue of the web sidebar tool panel + right-click
// context menu.
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'charts';

type NodeShape = 'rectangle' | 'circle' | 'diamond' | 'cylinder' | 'triangle' | 'parallelogram';

interface ChartNodeData {
  label: string;
  width?: number;
  height?: number;
  color?: string;
  fontColor?: string;
}

interface ChartNode {
  id: string;
  type: NodeShape;
  position: { x: number; y: number };
  data: ChartNodeData;
}

interface ChartEdge {
  id: string;
  source: string;
  target: string;
}

interface Chart {
  id: string;
  name: string;
  nodes: ChartNode[];
  edges: ChartEdge[];
  lastModified: string;
}

const SHAPE_OPTIONS: { type: NodeShape; label: string }[] = [
  { type: 'rectangle', label: 'Rectangle' },
  { type: 'circle', label: 'Circle' },
  { type: 'diamond', label: 'Diamond' },
  { type: 'cylinder', label: 'Cylinder' },
  { type: 'triangle', label: 'Triangle' },
  { type: 'parallelogram', label: 'Parallelogram' },
];

const DEFAULT_SIZES: Record<NodeShape, { width: number; height: number }> = {
  rectangle: { width: 140, height: 80 },
  circle: { width: 110, height: 110 },
  diamond: { width: 110, height: 110 },
  cylinder: { width: 100, height: 130 },
  triangle: { width: 110, height: 96 },
  parallelogram: { width: 150, height: 80 },
};

const COLOR_SWATCHES = [
  '#fecaca', '#fca5a5', '#ef4444',
  '#fed7aa', '#fdba74', '#f97316',
  '#fef08a', '#fde047', '#eab308',
  '#bbf7d0', '#86efac', '#22c55e',
  '#a5f3fc', '#67e8f9', '#06b6d4',
  '#bfdbfe', '#93c5fd', '#3b82f6',
  '#ddd6fe', '#c4b5fd', '#8b5cf6',
  '#fbcfe8', '#f9a8d4', '#ec4899',
  colors.glassFill, colors.textFaint, colors.white,
];

function generateChartId(): string {
  return `chart-${Date.now()}`;
}

function generateNodeId(): string {
  return `node_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diffMs = Math.max(0, now - then);
  const diffSec = Math.round(diffMs / 1000);
  if (diffSec < 60) return 'just now';
  const diffMin = Math.round(diffSec / 60);
  if (diffMin < 60) return `${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} hour${diffHr === 1 ? '' : 's'} ago`;
  const diffDay = Math.round(diffHr / 24);
  if (diffDay < 30) return `${diffDay} day${diffDay === 1 ? '' : 's'} ago`;
  const diffMonth = Math.round(diffDay / 30);
  if (diffMonth < 12) return `${diffMonth} month${diffMonth === 1 ? '' : 's'} ago`;
  const diffYear = Math.round(diffMonth / 12);
  return `${diffYear} year${diffYear === 1 ? '' : 's'} ago`;
}

async function loadCharts(): Promise<Chart[]> {
  try {
    const raw = await AsyncStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as Chart[]) : [];
  } catch {
    return [];
  }
}

async function saveCharts(charts: Chart[]): Promise<void> {
  await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(charts));
}

// ----- shape rendering -----

function ShapeNode({
  node,
  selected,
  onSelect,
  onDragMove,
  onDragEnd,
  connectMode,
}: {
  node: ChartNode;
  selected: boolean;
  onSelect: () => void;
  onDragMove: (dx: number, dy: number) => void;
  onDragEnd: () => void;
  connectMode: boolean;
}) {
  const width = node.data.width ?? DEFAULT_SIZES[node.type].width;
  const height = node.data.height ?? DEFAULT_SIZES[node.type].height;
  const fill = node.data.color ?? colors.white;
  const strokeColor = selected ? colors.blueText : colors.blue;

  const panResponder = useRef(
    PanResponder.create({
      onStartShouldSetPanResponder: () => true,
      onMoveShouldSetPanResponder: (_evt, gesture) =>
        Math.abs(gesture.dx) > 3 || Math.abs(gesture.dy) > 3,
      onPanResponderGrant: () => onSelect(),
      onPanResponderMove: (_evt, gesture) => {
        if (!connectMode) onDragMove(gesture.dx, gesture.dy);
      },
      onPanResponderRelease: () => onDragEnd(),
      onPanResponderTerminate: () => onDragEnd(),
    })
  ).current;

  let shapeEl: React.ReactNode;
  const cx = width / 2;
  const cy = height / 2;
  if (node.type === 'circle') {
    shapeEl = (
      <Ellipse cx={cx} cy={cy} rx={cx - 2} ry={cy - 2} fill={fill} stroke={strokeColor} strokeWidth={2} />
    );
  } else if (node.type === 'diamond') {
    const pts = `${cx},2 ${width - 2},${cy} ${cx},${height - 2} 2,${cy}`;
    shapeEl = <Polygon points={pts} fill={fill} stroke={strokeColor} strokeWidth={2} />;
  } else if (node.type === 'triangle') {
    const pts = `${cx},2 ${width - 2},${height - 2} 2,${height - 2}`;
    shapeEl = <Polygon points={pts} fill={fill} stroke={strokeColor} strokeWidth={2} />;
  } else if (node.type === 'parallelogram') {
    const skew = width * 0.15;
    const pts = `${skew},2 ${width - 2},2 ${width - skew},${height - 2} 2,${height - 2}`;
    shapeEl = <Polygon points={pts} fill={fill} stroke={strokeColor} strokeWidth={2} />;
  } else if (node.type === 'cylinder') {
    const ellipseRy = Math.min(14, height * 0.15);
    shapeEl = (
      <G>
        <Rect x={2} y={ellipseRy} width={width - 4} height={height - ellipseRy * 2} fill={fill} stroke={strokeColor} strokeWidth={2} />
        <Ellipse cx={cx} cy={ellipseRy} rx={cx - 2} ry={ellipseRy} fill={fill} stroke={strokeColor} strokeWidth={2} />
        <Path
          d={`M 2 ${height - ellipseRy} A ${cx - 2} ${ellipseRy} 0 0 0 ${width - 2} ${height - ellipseRy}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={2}
        />
      </G>
    );
  } else {
    shapeEl = (
      <Rect x={2} y={2} width={width - 4} height={height - 4} rx={8} fill={fill} stroke={strokeColor} strokeWidth={2} />
    );
  }

  return (
    <View
      {...panResponder.panHandlers}
      style={{
        position: 'absolute',
        left: node.position.x,
        top: node.position.y,
        width,
        height,
      }}
    >
      <Pressable onPress={onSelect} style={StyleSheet.absoluteFill}>
        <Svg width={width} height={height}>
          {shapeEl}
        </Svg>
        <View style={styles.nodeLabelWrap} pointerEvents="none">
          <Text
            style={[styles.nodeLabelText, { color: node.data.fontColor ?? colors.text }]}
            numberOfLines={2}
          >
            {node.data.label}
          </Text>
        </View>
        {selected ? <View style={styles.nodeSelectedRing} pointerEvents="none" /> : null}
      </Pressable>
    </View>
  );
}

function EdgesLayer({ nodes, edges }: { nodes: ChartNode[]; edges: ChartEdge[] }) {
  const centerOf = (n: ChartNode) => {
    const w = n.data.width ?? DEFAULT_SIZES[n.type].width;
    const h = n.data.height ?? DEFAULT_SIZES[n.type].height;
    return { x: n.position.x + w / 2, y: n.position.y + h / 2 };
  };
  return (
    <Svg style={StyleSheet.absoluteFill} pointerEvents="none">
      {edges.map((e) => {
        const source = nodes.find((n) => n.id === e.source);
        const target = nodes.find((n) => n.id === e.target);
        if (!source || !target) return null;
        const a = centerOf(source);
        const b = centerOf(target);
        return (
          <Line
            key={e.id}
            x1={a.x}
            y1={a.y}
            x2={b.x}
            y2={b.y}
            stroke={colors.slate}
            strokeWidth={2}
          />
        );
      })}
    </Svg>
  );
}

// ----- editor -----

function ChartEditor({
  chart,
  onBack,
  onChange,
}: {
  chart: Chart;
  onBack: () => void;
  onChange: (chart: Chart) => void;
}) {
  const [name, setName] = useState(chart.name);
  const [nodes, setNodes] = useState<ChartNode[]>(chart.nodes);
  const [edges, setEdges] = useState<ChartEdge[]>(chart.edges);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [connectMode, setConnectMode] = useState(false);
  const [connectFrom, setConnectFrom] = useState<string | null>(null);
  const [canvasSize, setCanvasSize] = useState({ width: 320, height: 420 });
  const dragOrigin = useRef<{ x: number; y: number } | null>(null);

  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const queueSave = useCallback(
    (nextName: string, nextNodes: ChartNode[], nextEdges: ChartEdge[]) => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = setTimeout(() => {
        onChange({
          ...chart,
          name: nextName,
          nodes: nextNodes,
          edges: nextEdges,
          lastModified: new Date().toISOString(),
        });
      }, 500);
    },
    [chart, onChange]
  );

  useEffect(() => {
    queueSave(name, nodes, edges);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, nodes, edges]);

  const onCanvasLayout = (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    if (width > 0 && height > 0) setCanvasSize({ width, height });
  };

  const addNode = (type: NodeShape) => {
    const { width, height } = DEFAULT_SIZES[type];
    const newNode: ChartNode = {
      id: generateNodeId(),
      type,
      position: {
        x: Math.max(8, Math.random() * (canvasSize.width - width - 16)),
        y: Math.max(8, Math.random() * (canvasSize.height - height - 16)),
      },
      data: { label: `New ${type}`, width, height },
    };
    setNodes((prev) => [...prev, newNode]);
    setSelectedId(newNode.id);
  };

  const updateNodeData = (id: string, patch: Partial<ChartNodeData>) => {
    setNodes((prev) => prev.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...patch } } : n)));
  };

  const selectNode = (id: string) => {
    if (connectMode) {
      if (!connectFrom) {
        setConnectFrom(id);
      } else if (connectFrom !== id) {
        const edgeId = `edge_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        setEdges((prev) => [...prev, { id: edgeId, source: connectFrom, target: id }]);
        setConnectFrom(null);
        setConnectMode(false);
      }
      return;
    }
    setSelectedId(id);
  };

  const moveNode = (id: string, dx: number, dy: number) => {
    if (!dragOrigin.current) {
      const node = nodes.find((n) => n.id === id);
      dragOrigin.current = node ? { x: node.position.x, y: node.position.y } : { x: 0, y: 0 };
    }
    const origin = dragOrigin.current;
    setNodes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, position: { x: origin.x + dx, y: origin.y + dy } } : n))
    );
  };

  const endDrag = () => {
    dragOrigin.current = null;
  };

  const deleteNode = (id: string) => {
    setNodes((prev) => prev.filter((n) => n.id !== id));
    setEdges((prev) => prev.filter((e) => e.source !== id && e.target !== id));
    if (selectedId === id) setSelectedId(null);
  };

  const selectedNode = nodes.find((n) => n.id === selectedId) ?? null;

  const showNodeMenu = (node: ChartNode) => {
    Alert.alert(node.data.label || 'Shape', undefined, [
      {
        text: 'Rename',
        onPress: () => {
          Alert.prompt?.(
            'Rename shape',
            undefined,
            (text) => updateNodeData(node.id, { label: text }),
            'plain-text',
            node.data.label
          );
        },
      },
      { text: 'Delete', style: 'destructive', onPress: () => deleteNode(node.id) },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  return (
    <View style={styles.editorRoot}>
      <View style={styles.editorHeaderRow}>
        <Pressable style={styles.backButton} onPress={onBack}>
          <Text style={styles.backButtonText}>‹ Back to Charts</Text>
        </Pressable>
      </View>
      <TextInput
        style={styles.chartNameInput}
        value={name}
        onChangeText={setName}
        placeholder="Enter chart name…"
        placeholderTextColor={colors.slate}
      />

      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.toolShelf} contentContainerStyle={styles.toolShelfContent}>
        {SHAPE_OPTIONS.map((opt) => (
          <Pressable key={opt.type} style={styles.shapeAddButton} onPress={() => addNode(opt.type)}>
            <Text style={styles.shapeAddText}>+ {opt.label}</Text>
          </Pressable>
        ))}
        <Pressable
          style={[styles.shapeAddButton, connectMode && styles.shapeAddButtonActive]}
          onPress={() => {
            setConnectMode((v) => !v);
            setConnectFrom(null);
          }}
        >
          <Text style={[styles.shapeAddText, connectMode && styles.shapeAddTextActive]}>
            {connectMode ? (connectFrom ? 'Tap target…' : 'Tap source…') : 'Connect'}
          </Text>
        </Pressable>
      </ScrollView>

      {selectedNode ? (
        <View style={styles.selectedBar}>
          <Text style={styles.selectedBarLabel} numberOfLines={1}>
            {selectedNode.data.label || 'Shape'}
          </Text>
          <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.swatchRow}>
            {COLOR_SWATCHES.map((c) => (
              <Pressable
                key={c}
                style={[styles.swatch, { backgroundColor: c }]}
                onPress={() => updateNodeData(selectedNode.id, { color: c })}
              />
            ))}
          </ScrollView>
          <Pressable style={styles.menuButton} onPress={() => showNodeMenu(selectedNode)}>
            <Text style={styles.menuButtonText}>⋯</Text>
          </Pressable>
        </View>
      ) : null}

      <View style={styles.canvas} onLayout={onCanvasLayout}>
        <EdgesLayer nodes={nodes} edges={edges} />
        {nodes.map((node) => (
          <ShapeNode
            key={node.id}
            node={node}
            selected={node.id === selectedId || node.id === connectFrom}
            connectMode={connectMode}
            onSelect={() => selectNode(node.id)}
            onDragMove={(dx, dy) => moveNode(node.id, dx, dy)}
            onDragEnd={endDrag}
          />
        ))}
        {nodes.length === 0 ? (
          <View style={styles.canvasEmpty} pointerEvents="none">
            <Text style={styles.canvasEmptyText}>Add a shape to get started.</Text>
          </View>
        ) : null}
      </View>
    </View>
  );
}

// ----- list + section root -----

export function ChartsSection({ userId: _userId }: { userId: string | undefined }) {
  const [charts, setCharts] = useState<Chart[]>([]);
  const [loading, setLoading] = useState(true);
  const [openChartId, setOpenChartId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    const stored = await loadCharts();
    setCharts(stored);
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const sortedCharts = useMemo(
    () =>
      [...charts].sort(
        (a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
      ),
    [charts]
  );

  const handleCreate = async () => {
    const newChart: Chart = {
      id: generateChartId(),
      name: 'New Chart',
      nodes: [
        {
          id: generateNodeId(),
          type: 'rectangle',
          position: { x: 24, y: 24 },
          data: { label: 'Start Here', width: 140, height: 80 },
        },
      ],
      edges: [],
      lastModified: new Date().toISOString(),
    };
    const next = [newChart, ...charts];
    setCharts(next);
    await saveCharts(next);
    setOpenChartId(newChart.id);
  };

  const handleDelete = (chart: Chart) => {
    Alert.alert('Delete this chart?', `This permanently deletes "${chart.name}".`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          const next = charts.filter((c) => c.id !== chart.id);
          setCharts(next);
          await saveCharts(next);
        },
      },
    ]);
  };

  const handleChartChange = useCallback(
    (updated: Chart) => {
      setCharts((prev) => {
        const idx = prev.findIndex((c) => c.id === updated.id);
        const next = idx > -1 ? prev.map((c) => (c.id === updated.id ? updated : c)) : [updated, ...prev];
        void saveCharts(next);
        return next;
      });
    },
    []
  );

  const openChart = sortedCharts.find((c) => c.id === openChartId) ?? null;

  if (loading) {
    return (
      <View style={styles.center}>
        <Text style={styles.empty}>Loading charts…</Text>
      </View>
    );
  }

  if (openChart) {
    return <ChartEditor chart={openChart} onBack={() => setOpenChartId(null)} onChange={handleChartChange} />;
  }

  return (
    <ScrollView style={styles.root} contentContainerStyle={styles.listContent}>
      <View style={styles.listHeaderRow}>
        <Text style={styles.listHeading}>Your Charts</Text>
        <Pressable style={styles.createButton} onPress={handleCreate}>
          <Text style={styles.createButtonText}>+ New Chart</Text>
        </Pressable>
      </View>
      <Text style={styles.listSubheading}>Select a chart to edit or create a new one.</Text>

      {sortedCharts.length === 0 ? (
        <View style={styles.emptyBox}>
          <Text style={styles.empty}>No charts found. Create one to get started!</Text>
        </View>
      ) : (
        <View style={styles.list}>
          {sortedCharts.map((chart) => (
            <Pressable
              key={chart.id}
              style={styles.chartRow}
              onPress={() => setOpenChartId(chart.id)}
              onLongPress={() => handleDelete(chart)}
            >
              <View style={styles.chartRowMain}>
                <Text style={styles.chartRowTitle} numberOfLines={1}>
                  {chart.name}
                </Text>
                <Text style={styles.chartRowMeta}>
                  Last modified: {formatRelative(chart.lastModified)}
                </Text>
              </View>
              <Pressable
                style={styles.deleteButton}
                onPress={(e) => {
                  e.stopPropagation();
                  handleDelete(chart);
                }}
                hitSlop={8}
              >
                <Text style={styles.deleteButtonText}>Delete</Text>
              </Pressable>
            </Pressable>
          ))}
        </View>
      )}
      <Text style={styles.hint}>Tap to open · long-press for delete</Text>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1 },
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 24 },
  empty: { color: colors.slate, fontStyle: 'italic', textAlign: 'center', fontSize: 13 },
  emptyBox: { alignItems: 'center', paddingVertical: 32 },

  // ----- list -----
  listContent: { padding: 12, paddingBottom: 24 },
  listHeaderRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  listHeading: { fontSize: 17, fontWeight: '900', color: colors.text },
  listSubheading: { fontSize: 12, color: colors.slate, marginTop: 4, marginBottom: 12 },
  createButton: {
    backgroundColor: colors.lime,
    borderRadius: radii.tile,
    ...neu.raisedSm,
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  createButtonText: { fontWeight: '900', color: colors.blueText, fontSize: 12 },
  list: { gap: 8 },
  chartRow: {
    backgroundColor: colors.glassFill,
    borderRadius: radii.tile,
    ...neu.raised,
    padding: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  chartRowMain: { flex: 1 },
  chartRowTitle: { fontSize: 14, fontWeight: '700', color: colors.text, fontFamily: fonts.title, textTransform: 'uppercase' },
  chartRowMeta: { fontSize: 11, color: colors.slate, marginTop: 2 },
  deleteButton: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: radii.pill,
    backgroundColor: colors.glassFill,
    ...neu.raisedSm,
  },
  deleteButtonText: { color: colors.danger, fontSize: 11, fontWeight: '800' },
  hint: { fontSize: 11, color: colors.slate, textAlign: 'center', marginTop: 14 },

  // ----- editor -----
  editorRoot: { flex: 1, padding: 12 },
  editorHeaderRow: { marginBottom: 8 },
  backButton: { alignSelf: 'flex-start' },
  backButtonText: { color: colors.blueText, fontWeight: '700', fontSize: 14 },
  chartNameInput: {
    fontSize: 18,
    fontWeight: '900',
    color: colors.text,
    backgroundColor: colors.inputFill,
    ...neu.insetDeep,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 7,
    marginBottom: 10,
  },
  toolShelf: { flexGrow: 0, marginBottom: 8 },
  toolShelfContent: { gap: 8, paddingRight: 8 },
  shapeAddButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: radii.pill,
    backgroundColor: colors.glassFill,
    ...neu.raisedSm,
  },
  shapeAddButtonActive: { backgroundColor: colors.lime, borderColor: colors.blue },
  shapeAddText: { fontSize: 12, fontWeight: '700', color: colors.text },
  shapeAddTextActive: { color: colors.blueText },
  selectedBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    backgroundColor: colors.glassFill,
    borderRadius: radii.tile,
    ...neu.raised,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 8,
  },
  selectedBarLabel: { fontSize: 12, fontWeight: '700', color: colors.text, width: 80 },
  swatchRow: { flex: 1 },
  swatch: {
    width: 22,
    height: 22,
    borderRadius: 11,
    marginRight: 6,
  },
  menuButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.glassFill,
    ...neu.raisedSm,
  },
  menuButtonText: { fontSize: 16, fontWeight: '900', color: colors.slate },
  canvas: {
    flex: 1,
    borderRadius: radii.panel,
    backgroundColor: colors.glassFillStrong,
    ...neu.inset,
    overflow: 'hidden',
    position: 'relative',
  },
  canvasEmpty: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  canvasEmptyText: { color: colors.slate, fontStyle: 'italic', fontSize: 13 },
  nodeLabelWrap: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 6,
  },
  nodeLabelText: { fontSize: 12, fontWeight: '700', textAlign: 'center' },
  nodeSelectedRing: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: colors.blueText,
  },
});
